/**
 * Hook to extract feature flag context from existing stores
 * Automatically maps user and account data to feature flag context
 */

import { useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useUserStore } from 'store/user-store'
import { useAccountStore } from 'store/account-store'
import { useAdminStore } from 'store/admin-store'
import { useUiStore } from 'store/ui-store'
import { EvaluationContext } from 'api-models'

export const useAutomaticFeatureFlagContext = (): EvaluationContext => {
	// Get user data
	const { user } = useUserStore(
		useShallow(state => ({
			user: state.user
		}))
	)

	// Get account data
	const { account, subscription } = useAccountStore(
		useShallow(state => ({
			account: state.account,
			subscription: state.subscription
		}))
	)

	// Get admin/impersonation data
	const { imperId, impersonation } = useAdminStore(
		useShallow(state => ({
			imperId: state.imperId,
			impersonation: state.impersonation
		}))
	)

	// Get UI context
	const { context: uiContext } = useUiStore(
		useShallow(state => ({
			context: state.context
		}))
	)

	return useMemo(() => {
		const featureFlagContext: EvaluationContext = {
			userId: imperId || user?.id,
			sessionId: user?.id,
			accountId: account?.id,
			email: user?.email,
			attributes: {
				// User properties
				plan: subscription?.plan?.tier || account?.billing?.subscription?.plan?.tier,
				tier: subscription?.plan?.tier || account?.billing?.subscription?.plan?.tier,
				region: user?.country || undefined,
				beta: false,
				createdAt: user?.created_at.toString() || undefined,
				isPaying: user?.is_paying || false,
				isManaging: user?.is_managing || false,
				usedTrial: user?.used_trial || false,

				// Account properties
				accountStatus: account?.status,
				accountCreatedAt: account?.created_at.toString() || undefined,
				monthlySpend: account?.reported_spend || 0,

				// Context properties
				currentContext: uiContext,
				impersonation: impersonation || false,

				// Type properties
				userType: user?.admin ? 'admin' : 'user',
				accountType:
					subscription?.plan?.tier ||
					account?.billing?.subscription?.plan?.tier ||
					'free'
			}
		}
		return featureFlagContext
	}, [user, account, subscription, imperId, impersonation, uiContext])
}

